import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, setPersistence, browserLocalPersistence, connectAuthEmulator } from 'firebase/auth';
import { 
  getFirestore, 
  enableIndexedDbPersistence, 
  disableNetwork, 
  enableNetwork,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  clearIndexedDbPersistence,
  connectFirestoreEmulator,
  getDoc,
  addDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getAnalytics } from 'firebase/analytics';

const isBrowser = typeof window !== 'undefined';
const hasNavigator = typeof navigator !== 'undefined';
const isTestMode =
  (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test') ||
  (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test');

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDxbrQTxet2oSXsd-bc0I3r1OEGU8gaJNE",
  authDomain: "bloom-cd83b.firebaseapp.com",
  projectId: "bloom-cd83b",
  storageBucket: "bloom-cd83b.appspot.com",
  messagingSenderId: "686836133633",
  appId: "1:686836133633:web:ccdaa2a9b26a8fbf807500",
  measurementId: "G-ZKJ3CMQKNB"
};

// Initialize Firebase - simplified approach
console.log('Setting up Firebase with config:', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain
});

// Globals for Firebase services
let app;
let auth;
let firestore;
let storage;
let analytics;
let isOnline = hasNavigator ? navigator.onLine : true;
let initializationStatus = {
  initialized: false,
  error: null,
  services: {
    app: false,
    auth: false,
    firestore: false,
    storage: false,
    analytics: false,
    persistence: false
  }
};

// Initialize Firebase with simplified, sequential approach
async function initializeFirebase() {
  try {
    console.log('Starting Firebase initialization...');
    
    // Check for existing instances to avoid duplicates
    const existingApps = getApps();
    console.log('Existing Firebase apps:', existingApps.length);
    
    if (existingApps.length > 0) {
      console.log('Using existing Firebase app instance');
      app = existingApps[0];
      initializationStatus.services.app = true;
    } else {
      console.log('Creating new Firebase app instance');
      try {
        app = initializeApp(firebaseConfig);
        console.log('New Firebase app created successfully');
        initializationStatus.services.app = true;
      } catch (error) {
        console.error('Failed to create Firebase app:', error);
        throw error;
      }
    }
    
    // Initialize auth
    try {
      auth = getAuth(app);
      console.log('Firebase Auth initialized successfully');
      initializationStatus.services.auth = true;
      
      // Set auth persistence to LOCAL - this is critical for data persistence
      try {
        setPersistence(auth, browserLocalPersistence)
          .then(() => {
            console.log('Firebase Auth persistence set to LOCAL successfully');
          })
          .catch((error) => {
            console.error('Error setting persistence:', error);
          });
      } catch (persistenceError) {
        console.error('Failed to set auth persistence:', persistenceError);
      }
    } catch (error) {
      console.error('Failed to initialize Firebase Auth:', error);
      throw error;
    }
    
    // Initialize Firestore
    try {
      firestore = getFirestore(app);
      console.log('Firebase Firestore initialized successfully');
      initializationStatus.services.firestore = true;
    } catch (error) {
      console.error('Failed to initialize Firestore:', error);
      throw error;
    }
    
    // Initialize storage
    try {
      storage = getStorage(app);
      console.log('Firebase Storage initialized successfully');
      initializationStatus.services.storage = true;
    } catch (error) {
      console.error('Failed to initialize Storage:', error);
      throw error;
    }
    
    // Initialize analytics when possible
    try {
      if (isBrowser) {
        analytics = getAnalytics(app);
        console.log('Firebase Analytics initialized successfully');
        initializationStatus.services.analytics = true;
      }
    } catch (error) {
      console.warn('Analytics not available:', error.message);
    }
    
    // Check for browser internal DB issues before enabling persistence
    const checkForInternalDBIssues = async () => {
      if (typeof indexedDB === 'undefined') {
        return { canProceed: true, hasIssues: true, reason: 'IndexedDB not available in this browser' };
      }
      
      return new Promise(resolve => {
        try {
          // Try opening a test database
          const testDBOpen = indexedDB.open('firestore-check-' + Date.now());
          testDBOpen.onupgradeneeded = () => {
            try {
              // Create an object store
              const db = testDBOpen.result;
              db.createObjectStore('test-store');
            } catch (e) {
              console.error('Error during test DB setup:', e);
            }
          };
          
          testDBOpen.onsuccess = () => {
            try {
              // Close and delete the test database
              const db = testDBOpen.result;
              db.close();
              indexedDB.deleteDatabase('firestore-check-' + Date.now());
              resolve({ canProceed: true, hasIssues: false });
            } catch (e) {
              resolve({ canProceed: true, hasIssues: true, reason: e.message });
            }
          };
          
          testDBOpen.onerror = () => {
            console.error('IndexedDB test failed:', testDBOpen.error);
            resolve({ canProceed: true, hasIssues: true, reason: testDBOpen.error?.message || 'Unknown IndexedDB error' });
          };
          
          // Set a timeout in case the operation hangs
          setTimeout(() => {
            resolve({ canProceed: true, hasIssues: true, reason: 'IndexedDB test timed out' });
          }, 3000);
        } catch (e) {
          console.error('Error checking IndexedDB:', e);
          resolve({ canProceed: true, hasIssues: true, reason: e.message });
        }
      });
    };
    
    // If we have network connectivity, ensure network is enabled
    if (!hasNavigator || navigator.onLine) {
      try {
        await enableNetwork(firestore);
        console.log('Firestore network enabled successfully');
      } catch (error) {
        console.error('Failed to enable Firestore network:', error);
        throw error;
      }
    } else {
      console.warn('Device is offline during initialization');
    }
    
    // Check for IndexedDB issues before enabling persistence
    const dbCheck = await checkForInternalDBIssues();
    console.log('IndexedDB check results:', dbCheck);
    
    // Enable persistence only if IndexedDB check passed
    if (typeof window !== 'undefined' && firestore && !dbCheck.hasIssues) {
      try {
        console.log('Attempting to enable Firestore persistence...');
        // Use a try-catch block with a timeout to prevent potential hanging
        const persistencePromise = new Promise((resolve, reject) => {
          // Set a timeout to prevent hanging if persistence initialization takes too long
          const timeoutId = setTimeout(() => {
            reject(new Error('Persistence initialization timed out'));
          }, 10000); // 10 seconds timeout
          
          enableIndexedDbPersistence(firestore)
            .then(() => {
              clearTimeout(timeoutId);
              console.log('Firestore persistence enabled successfully');
              initializationStatus.services.persistence = true;
              resolve();
            })
            .catch((error) => {
              clearTimeout(timeoutId);
              reject(error);
            });
        });
        
        await persistencePromise;
      } catch (error) {
        if (error.code === 'failed-precondition') {
          console.warn('Firestore persistence unavailable: Multiple tabs open');
        } else if (error.code === 'unimplemented') {
          console.warn('Firestore persistence unavailable: Browser not supported');
        } else {
          console.error('Error enabling Firestore persistence:', error);
          
          // Make sure the app can still work without persistence
          console.log('Proceeding without persistence');
        }
        
        // Continue initialization without blocking on persistence
        initializationStatus.services.persistence = false;
      }
    } else if (dbCheck.hasIssues) {
      console.warn('Skipping Firestore persistence due to IndexedDB issues:', dbCheck.reason);
      initializationStatus.services.persistence = false;
    }
    
    // Test the connection
    const connectionStatus = await checkFirestoreConnection();
    console.log('Initial connection status:', connectionStatus);
    
    if (!connectionStatus.connected) {
      throw new Error(`Failed to establish Firestore connection: ${connectionStatus.error}`);
    }
    
    initializationStatus.initialized = true;
    console.log('Firebase initialization completed successfully');
    return true;
  } catch (error) {
    console.error('Error initializing Firebase:', error);
    initializationStatus.error = error;
    return false;
  }
}

// Export initialization status
export const getInitializationStatus = () => ({ ...initializationStatus });

// Simplified network status monitoring
if (isBrowser) {
  window.addEventListener('online', () => {
    console.log('App is online');
    isOnline = true;
    if (firestore) {
      enableNetwork(firestore).then(() => {
        console.log('Firestore network connection restored');
      }).catch(error => {
        console.error('Error enabling Firestore network:', error);
      });
    }
  });

  window.addEventListener('offline', () => {
    console.log('App is offline');
    isOnline = false;
  });
}

// Function to check Firestore connection
export const checkFirestoreConnection = async () => {
  if (!firestore) {
    return { connected: false, error: 'Firestore is not initialized' };
  }
  
  if (hasNavigator && !navigator.onLine) {
    return { 
      connected: false, 
      error: 'Device is offline. Please check your internet connection.',
      code: 'network-offline'
    };
  }
  
  try {
    const testQuery = query(collection(firestore, 'users'), limit(1));
    await getDocs(testQuery);
    return { connected: true };
  } catch (error) {
    console.error('Firestore connection test failed:', error);
    
    if (error.code === 'permission-denied') {
      return { connected: true, error: 'Authentication required' };
    }
    
    return { 
      connected: false, 
      error: error.message || 'Failed to connect to Firestore',
      code: error.code || 'unknown-error'
    };
  }
};

// Reset Firebase connection - can be called when experiencing issues
export const resetFirebaseConnection = async () => {
  if (!firestore) return { success: false, error: 'Firestore not initialized' };
  
  try {
    console.log('Resetting Firebase connection...');
    
    let requiresReload = false;
    
    // Check for specific error conditions that might indicate we need a full page reload
    try {
      // Test if we can access Firestore at all
      const testQuery = query(collection(firestore, 'users'), limit(1));
      await getDocs(testQuery);
    } catch (testError) {
      console.error('Pre-reset connection test failed:', testError);
      
      // For development environment, be more aggressive with reloads
      if (window.location.hostname === 'localhost' || 
          (testError.message && testError.message.includes('INTERNAL ASSERTION FAILED'))) {
        console.log('Detected error in development environment or internal assertion error');
        requiresReload = true;
      }
    }
    
    // For localhost, try to clear the cache first which often helps with internal assertion errors
    if (window.location.hostname === 'localhost') {
      try {
        await clearFirestoreCache();
        console.log('Cleared Firestore cache in development environment');
      } catch (cacheError) {
        console.error('Failed to clear cache:', cacheError);
        // If we can't clear the cache, we definitely need a reload
        requiresReload = true;
      }
    }
    
    // Disable network
    try {
      await disableNetwork(firestore);
      console.log('Network disabled temporarily');
    } catch (disableError) {
      console.error('Failed to disable network:', disableError);
      // If we can't disable the network, suggest a page reload
      requiresReload = true;
    }
    
    // Wait longer to ensure state is properly cleaned up
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Re-enable network
    try {
      await enableNetwork(firestore);
      console.log('Network re-enabled');
    } catch (enableError) {
      console.error('Failed to re-enable network:', enableError);
      requiresReload = true;
    }
    
    // Only test the connection if we successfully toggled the network
    if (!requiresReload) {
      try {
        // Test the connection with a simple query (no ordering)
        const testQuery = query(collection(firestore, 'users'), limit(1));
        await getDocs(testQuery);
        console.log('Connection test successful after reset');
      } catch (testError) {
        console.warn('Connection test after reset encountered issue:', testError);
        // If we get another internal assertion error after reset, we definitely need a reload
        if (testError.message && testError.message.includes('INTERNAL ASSERTION FAILED')) {
          requiresReload = true;
        }
      }
    }
    
    return { 
      success: !requiresReload, 
      requiresReload: requiresReload
    };
  } catch (error) {
    console.error('Error resetting Firestore connection:', error);
    return { 
      success: false, 
      error: error.message || 'Unknown error',
      requiresReload: true
    };
  }
};

// Anonymous auth helper
export const ensureAuthentication = async () => {
  if (!auth) return null;
  
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();
      if (user) {
        console.log('User is authenticated:', user.uid);
        resolve(user);
      } else {
        try {
          const result = await signInAnonymously(auth);
          console.log('Anonymous auth successful:', result.user.uid);
          resolve(result.user);
        } catch (error) {
          console.error('Anonymous auth failed:', error);
          resolve(null);
        }
      }
    });
  });
};

// Utility function to check network status
export const isNetworkAvailable = () => isOnline;

// Export initialized services
export { auth, firestore, storage, analytics, app, enableNetwork, disableNetwork };

// Logout helper
export const logout = async () => {
  if (!auth) return false;
  try {
    await auth.signOut();
    console.log('User logged out successfully');
    return true;
  } catch (error) {
    console.error('Logout error:', error);
    return false;
  }
};

// Initialize Firebase when this module is imported
const initPromise = isTestMode ? Promise.resolve(true) : initializeFirebase();
initPromise.then(success => {
  console.log('Firebase initialization complete, success:', success);
  if (!success) {
    console.error('Firebase initialization failed. Status:', getInitializationStatus());
  }
});

// Export initialization promise for components that need to wait for initialization
export const getInitializationPromise = () => initPromise;

// Function to clear Firestore persistence cache
export const clearFirestoreCache = async () => {
  if (!firestore) return { success: false, error: 'Firestore not initialized' };
  
  try {
    console.log('Attempting to clear Firestore persistence cache...');
    
    // First disable network to ensure no operations are in flight
    await disableNetwork(firestore);
    console.log('Network disabled before clearing cache');
    
    // Clear the persistence
    await clearIndexedDbPersistence(firestore);
    console.log('Successfully cleared Firestore persistence cache');
    
    // Re-enable network after clearing cache
    await enableNetwork(firestore);
    console.log('Network re-enabled after clearing cache');
    
    return { success: true };
  } catch (error) {
    console.error('Error clearing Firestore cache:', error);
    
    // If clearIndexedDbPersistence failed but we disabled the network,
    // make sure to re-enable it
    try {
      await enableNetwork(firestore);
      console.log('Re-enabled network after error');
    } catch (netError) {
      console.error('Failed to re-enable network:', netError);
    }
    
    return { 
      success: false, 
      error: error.message || 'Unknown error',
      code: error.code 
    };
  }
};

// Add error handling wrapper for Firestore
export const safeFirestore = (() => {
  // Create wrapper functions with error handling for common Firestore operations
  const wrapFirestoreOperation = async (operation, fallback = null) => {
    try {
      return await operation();
    } catch (error) {
      if (error.message && error.message.includes('INTERNAL ASSERTION FAILED')) {
        console.error('Firebase internal assertion error caught in wrapper:', error);
        
        // Try to reset connection
        try {
          console.log('Attempting to reset Firestore connection...');
          await resetFirebaseConnection();
        } catch (resetError) {
          console.error('Failed to reset connection:', resetError);
        }
        
        // Fallback to cached data or return null
        return fallback;
      }
      throw error;
    }
  };

  // Safe wrapper for getDoc that doesn't use onSnapshot internally
  const safeGetDoc = async (docRef) => {
    return wrapFirestoreOperation(async () => {
      return await getDoc(docRef);
    });
  };

  // Safe wrapper for getDocs that avoids internal assertion errors
  const safeGetDocs = async (query) => {
    return wrapFirestoreOperation(async () => {
      return await getDocs(query);
    }, { empty: true, docs: [] }); // Empty snapshot as fallback
  };

  // Safe version of addDoc to handle internal errors
  const safeAddDoc = async (collectionRef, data) => {
    return wrapFirestoreOperation(async () => {
      return await addDoc(collectionRef, data);
    });
  };

  return {
    getDoc: safeGetDoc,
    getDocs: safeGetDocs,
    addDoc: safeAddDoc,
    collection: (path) => collection(firestore, path),
    doc: (path, ...segments) => doc(firestore, path, ...segments),
    query,
    orderBy,
    limit,
    serverTimestamp
  };
})(); 
